import {
  type AttendanceTrend,
  hasLevel,
  ladderAccess,
  type ServiceSession,
  useAccess,
  useAttendanceTrend,
  useCampuses,
  useCheckinTimes,
  useDeleteService,
  useGroupAttendanceReport,
  useRecordService,
  useServiceSessions,
} from '@church/data'
import { SUGGESTED_HEADCOUNT_CATEGORIES } from '@church/domain'
import { faCheck, faChevronDown, faPlus, faTrashCan } from '@church/ui-icons'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { RequireAccess } from '../components/RequireAccess.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Field,
  IconButton,
  Input,
  listRow,
  PageHeader,
  PanelSection,
  Select,
  SidePanel,
  Spinner,
  Tabs,
} from '../components/ui.tsx'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { useListDisplay } from '../lib/display.ts'
import { PageAction } from '../components/PageAction.tsx'

/**
 * Attendance: the Sunday count and how it is trending, plus every group's
 * attendance side by side.
 *
 * Two things are counted here and they are not the same thing. A service has a
 * number; a group has a roster. Putting them on one screen without pretending
 * they are one metric is the whole design.
 */
export const Route = createFileRoute('/_authed/_shell/attendance')({
  beforeLoad: ({ context }) => {
    if (!hasLevel(ladderAccess(context.me), 'attendance', 'viewer')) {
      throw redirect({ to: '/' })
    }
  },
  component: AttendanceScreen,
})

const TABS = ['Services', 'Groups'] as const
type Tab = (typeof TABS)[number]

function AttendanceScreen() {
  const [tab, setTab] = useState<Tab>('Services')

  return (
    <div className="space-y-6">
      <PageHeader title="Attendance" />
      <Tabs tabs={TABS} value={tab} onChange={setTab}>
        {tab === 'Services' && <ServicesPanel />}
        {tab === 'Groups' && <GroupsPanel />}
      </Tabs>
    </div>
  )
}

/** What a service row shows on a phone; every field shows from `sm` up. */
const SERVICE_FIELDS = [
  { id: 'date', label: 'Date', default: true },
  { id: 'counts', label: 'Counts', default: false },
  { id: 'total', label: 'Total', default: true },
] as const

function ServicesPanel() {
  const [recording, setRecording] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const { data: trend } = useAttendanceTrend()
  const { sessions, isLoading, hasNextPage, fetchNextPage, error } = useServiceSessions()
  const remove = useDeleteService()
  const access = useAccess()
  const display = useListDisplay('attendance-services', SERVICE_FIELDS)
  const shown = sessions.find((session) => session.id === shownId)

  return (
    <div className="space-y-4">
      {trend && <TrendCard trend={trend} />}

      <ErrorNote error={error ?? remove.error} />

      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-content-default">Services</h2>
        <div className="flex items-center gap-1">
          <DisplayOptions fields={SERVICE_FIELDS} shown={display.shown} onChange={display.set} />
          <RequireAccess module="attendance" level="editor">
            <PageAction icon={faPlus} label="Record a service" onClick={() => setRecording(true)}>
              Record
            </PageAction>
          </RequireAccess>
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : sessions.length === 0 ? (
        <EmptyState
          title="No services recorded"
          hint="Record Sunday's count and the trend builds itself from there."
        />
      ) : (
        <Card bleed>
          <ul className="divide-y divide-border-subtle">
            {sessions.map((session) => (
              <li key={session.id}>
                <button
                  type="button"
                  onClick={() => {
                    setShownId(session.id)
                    setOpenId(session.id)
                  }}
                  className={`${listRow} flex w-full items-center gap-4 text-left hover:bg-surface-sunken`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-medium text-content-default">
                      {session.name}
                    </span>
                    <span
                      className={`block text-sm text-content-muted sm:hidden ${display.hide('date')}`}
                    >
                      {session.sessionDate}
                    </span>
                    <span
                      className={`block truncate text-xs text-content-muted ${display.hide('counts')}`}
                    >
                      {countsText(session)}
                      {session.checkedIn !== null && ` · ${session.checkedIn} checked in`}
                    </span>
                  </span>
                  <span className="hidden w-28 shrink-0 text-sm tabular-nums text-content-muted sm:block">
                    {session.sessionDate}
                  </span>
                  <span
                    className={`w-14 shrink-0 text-right text-lg font-semibold tabular-nums text-content-default ${display.hide('total')}`}
                  >
                    {session.total}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {hasNextPage && (
        <Button variant="ghost" icon={faChevronDown} onClick={() => void fetchNextPage()}>
          More
        </Button>
      )}

      <SidePanel
        open={openId !== null && shown !== undefined}
        onOpenChange={(next) => {
          if (!next) setOpenId(null)
        }}
        title={shown?.name ?? ''}
        description={shown?.sessionDate}
        footer={
          shown && access.has('attendance', 'administrator') ? (
            <ConfirmButton
              label="Delete count"
              icon={faTrashCan}
              variant="quietDanger"
              className="-ml-4"
              title={`Delete ${shown.name} on ${shown.sessionDate}?`}
              description="The count is removed from every trend and report."
              confirmLabel="Delete count"
              onConfirm={() => remove.mutate(shown.id, { onSuccess: () => setOpenId(null) })}
            />
          ) : undefined
        }
      >
        {shown && (
          <>
            <PanelSection title="Counts">
              <dl className="divide-y divide-border-subtle">
                {Object.entries(shown.counts).map(([category, count]) => (
                  <div key={category} className="flex items-baseline justify-between py-2">
                    <dt className="text-sm text-content-muted">{category}</dt>
                    <dd className="text-base tabular-nums text-content-default">{count}</dd>
                  </div>
                ))}
                <div className="flex items-baseline justify-between py-2">
                  <dt className="text-sm font-semibold text-content-default">Total</dt>
                  <dd className="text-2xl font-semibold tabular-nums text-content-default">
                    {shown.total}
                  </dd>
                </div>
              </dl>
            </PanelSection>
            {shown.checkedIn !== null && (
              <PanelSection
                title="Check-in"
                hint="From the check-in register, not part of the count above — the way Planning Center adds them to its totals."
              >
                <dl className="divide-y divide-border-subtle">
                  <div className="flex items-baseline justify-between py-2">
                    <dt className="text-sm text-content-muted">Checked in</dt>
                    <dd className="text-base tabular-nums text-content-default">
                      {shown.checkedIn}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between py-2">
                    <dt className="text-sm text-content-muted">Count and check-ins together</dt>
                    <dd className="text-base tabular-nums text-content-default">
                      {shown.total + shown.checkedIn}
                    </dd>
                  </div>
                </dl>
              </PanelSection>
            )}
            {access.has('attendance', 'editor') && (
              <p className="text-xs text-content-muted">
                To correct it, record the same service, campus and date again — the new count
                replaces this one.
              </p>
            )}
          </>
        )}
      </SidePanel>

      <RecordServiceForm open={recording} onDone={() => setRecording(false)} />
    </div>
  )
}

const countsText = (session: ServiceSession) => {
  const categories = Object.entries(session.counts)
  return categories.length === 0
    ? 'No breakdown'
    : categories.map(([category, count]) => `${category} ${count}`).join(' · ')
}

/**
 * The trend, as bars rather than a chart library. Twelve weeks of totals is a
 * shape you can read at a glance, and it does not need 40kB of canvas to draw.
 */
function TrendCard({ trend }: { trend: AttendanceTrend }) {
  const weeks = trend.weeks.slice(-12)
  if (weeks.length === 0) return null
  const peak = Math.max(...weeks.map((week) => week.total), 1)

  return (
    <Card className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap gap-6">
        <Stat label="Average week" value={String(trend.averageWeekly)} />
        <Stat
          label="Change"
          value={
            trend.changePercent === null
              ? '—'
              : `${trend.changePercent > 0 ? '+' : ''}${trend.changePercent}%`
          }
          tone={trend.changePercent === null ? 'muted' : trend.changePercent >= 0 ? 'good' : 'bad'}
        />
        <Stat label="Best week" value={trend.best ? String(trend.best.total) : '—'} />
      </div>

      <div className="flex items-end gap-1" aria-hidden>
        {weeks.map((week) => (
          <div
            key={week.weekStart}
            className="flex-1 rounded-t bg-brand-solid/70"
            style={{ height: `${Math.max(4, (week.total / peak) * 96)}px` }}
            title={`${week.weekStart}: ${week.total}`}
          />
        ))}
      </div>
      <p className="text-xs text-content-subtle">
        {weeks[0]?.weekStart} to {weeks.at(-1)?.weekStart} · comparison is the last four weeks
        against the four before them
      </p>

      {/* The same numbers, for anyone who cannot use the bars. */}
      <table className="sr-only">
        <caption>Weekly attendance totals</caption>
        <tbody>
          {weeks.map((week) => (
            <tr key={week.weekStart}>
              <th scope="row">{week.weekStart}</th>
              <td>{week.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

function Stat({
  label,
  value,
  tone = 'muted',
}: {
  label: string
  value: string
  tone?: 'muted' | 'good' | 'bad'
}) {
  const color =
    tone === 'good'
      ? 'text-success-content'
      : tone === 'bad'
        ? 'text-danger-solid'
        : 'text-content-default'
  return (
    <div>
      <p className={`text-xl font-semibold ${color}`}>{value}</p>
      <p className="text-xs text-content-muted">{label}</p>
    </div>
  )
}

function RecordServiceForm({ open, onDone }: { open: boolean; onDone: () => void }) {
  const record = useRecordService()
  const { data: campuses } = useCampuses()
  const access = useAccess()
  // The check-in services, to tie the count to one (only for those who see check-in).
  const { data: times } = useCheckinTimes(access.has('checkin', 'viewer'))
  const blank = {
    name: 'Sunday Service',
    sessionDate: new Date().toISOString().slice(0, 10),
    campusId: '',
    checkinTimeId: '',
  }
  const [form, setForm] = useState(blank)
  const [counts, setCounts] = useState<Record<string, string>>({ Adults: '', Children: '' })
  const [newCategory, setNewCategory] = useState('')
  const close = () => {
    setForm(blank)
    setCounts({ Adults: '', Children: '' })
    setNewCategory('')
    onDone()
  }

  const numeric = Object.fromEntries(
    Object.entries(counts)
      .filter(([, value]) => value.trim() !== '')
      .map(([category, value]) => [category, Number(value)]),
  )
  const total = Object.values(numeric).reduce((sum, value) => sum + value, 0)

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title="Record a service"
      description="Recording the same service, campus and date again replaces that count."
      primary={
        <Button
          icon={faCheck}
          className="py-1.5"
          pending={record.isPending}
          pendingLabel="Saving…"
          disabled={form.name.trim() === ''}
          onClick={() =>
            record.mutate(
              {
                name: form.name.trim(),
                sessionDate: form.sessionDate,
                campusId: form.campusId === '' ? null : form.campusId,
                checkinTimeId: form.checkinTimeId === '' ? null : form.checkinTimeId,
                counts: numeric,
              },
              { onSuccess: close },
            )
          }
        >
          Save count
        </Button>
      }
    >
      <Field label="Service" description="The same name each week keeps the trend comparable.">
        <Input
          value={form.name}
          onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date">
          <Input
            type="date"
            value={form.sessionDate}
            onChange={(event) => setForm((f) => ({ ...f, sessionDate: event.target.value }))}
          />
        </Field>
        <Field label="Campus">
          <Select
            value={form.campusId}
            onChange={(event) => setForm((f) => ({ ...f, campusId: event.target.value }))}
          >
            <option value="">All campuses</option>
            {(campuses ?? []).map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {times && times.length > 0 && (
        <Field label="Check-in service" description="Its check-ins show beside this count.">
          <Select
            value={form.checkinTimeId}
            onChange={(event) => setForm((f) => ({ ...f, checkinTimeId: event.target.value }))}
          >
            <option value="">None</option>
            {times.map((time) => (
              <option key={time.id} value={time.id}>
                {time.name}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <PanelSection title="Counts">
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(counts).map(([category, value]) => (
            <Field key={category} label={category}>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                className="text-lg tabular-nums"
                value={value}
                onChange={(event) =>
                  setCounts((current) => ({ ...current, [category]: event.target.value }))
                }
              />
            </Field>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {SUGGESTED_HEADCOUNT_CATEGORIES.filter((category) => !(category in counts)).map(
            (category) => (
              <Button
                key={category}
                variant="ghost"
                icon={faPlus}
                className="px-2 py-1 text-xs"
                onClick={() => setCounts((current) => ({ ...current, [category]: '' }))}
              >
                {category}
              </Button>
            ),
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Your own category"
            aria-label="Your own category"
            value={newCategory}
            onChange={(event) => setNewCategory(event.target.value)}
          />
          <IconButton
            icon={faPlus}
            label="Add category"
            variant="secondary"
            disabled={newCategory.trim() === ''}
            onClick={() => {
              setCounts((current) => ({ ...current, [newCategory.trim()]: '' }))
              setNewCategory('')
            }}
          />
        </div>
      </PanelSection>

      <p className="flex items-baseline justify-between border-t border-border-subtle pt-3 text-sm text-content-muted">
        Total
        <span className="text-2xl font-semibold tabular-nums text-content-default">{total}</span>
      </p>
      <ErrorNote error={record.error} />
    </SidePanel>
  )
}

/** What a group row shows on a phone; every field shows from `sm` up. */
const GROUP_FIELDS = [
  { id: 'type', label: 'Group type', default: true },
  { id: 'members', label: 'Members', default: false },
  { id: 'recorded', label: 'Meetings recorded', default: false },
  { id: 'average', label: 'Average attendance', default: false },
  { id: 'rate', label: 'Attendance rate', default: true },
] as const

/** Every group, worst attendance first — the server decides that order. */
function GroupsPanel() {
  const { data: rows, isPending } = useGroupAttendanceReport()
  const display = useListDisplay('attendance-groups', GROUP_FIELDS)

  if (isPending) return <Spinner />
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        title="No groups to report on"
        hint="Groups you can see the roster of appear here once they exist."
      />
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-content-muted">Lowest attendance first.</p>
        <DisplayOptions fields={GROUP_FIELDS} shown={display.shown} onChange={display.set} />
      </div>
      <Card bleed>
        <div className="hidden gap-4 border-b border-border-default bg-surface-heading px-4 py-2 text-xs font-medium uppercase tracking-wide text-content-muted sm:flex">
          <span className="flex-1">Group</span>
          <span className="w-20 text-right">Members</span>
          <span className="w-24 text-right">Recorded</span>
          <span className="w-20 text-right">Average</span>
          <span className="w-16 text-right">Rate</span>
        </div>
        <ul className="divide-y divide-border-subtle">
          {rows.map((row) => (
            <li key={row.groupId}>
              <Link
                to="/groups/$id"
                params={{ id: row.groupId }}
                className={`${listRow} flex items-center gap-4 no-underline hover:bg-surface-sunken`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base font-medium text-content-default">
                    {row.name}
                  </span>
                  <span className={`block text-sm text-content-muted ${display.hide('type')}`}>
                    {row.groupTypeName}
                  </span>
                  <span className="block text-xs text-content-muted sm:hidden">
                    {[
                      display.shown.members ? `${row.memberCount} members` : null,
                      display.shown.recorded
                        ? `${row.meetingsRecorded} of ${row.meetingsHeld} recorded`
                        : null,
                      display.shown.average ? `average ${row.averageAttendance}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="hidden w-20 text-right text-sm tabular-nums text-content-muted sm:block">
                  {row.memberCount}
                </span>
                <span className="hidden w-24 text-right text-sm whitespace-nowrap tabular-nums text-content-muted sm:block">
                  {row.meetingsRecorded} of {row.meetingsHeld}
                </span>
                <span className="hidden w-20 text-right text-sm tabular-nums text-content-muted sm:block">
                  {row.averageAttendance}
                </span>
                <span className={`w-16 shrink-0 text-right ${display.hide('rate')}`}>
                  {row.attendanceRate === null ? (
                    <span className="text-xs whitespace-nowrap text-content-subtle">no data</span>
                  ) : (
                    <Chip size="md">{row.attendanceRate}%</Chip>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}
